<script setup lang="ts">
import { PlusOutlined } from '@ant-design/icons-vue'
import {
  Button,
  Form,
  FormItem,
  Input,
  InputNumber,
  Modal,
  message,
  Select,
  Switch,
  TimePicker,
  Upload,
} from 'ant-design-vue'
import { computed, ref, watch } from 'vue'
import type { Company, Property } from '@/models'
import { getDisplayUrl, STORAGE_FOLDERS, uploadStorageFile } from '@/services/storage'

const props = defineProps<{
  modelValue: boolean
  companies: Company[]
  property: Partial<Property> | null
  saving?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  save: [value: Partial<Property>]
}>()

const dialog = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const form = ref<Record<string, any>>({ ...props.property })
const propertyTypes = ['House', 'Apartment', 'Condo', 'Villa', 'Townhouse']
const propertyStatus = ['active', 'draft', 'archived']
const imagePreview = ref('')
const pendingImageFile = ref<File | null>(null)
const fileList = ref<any[]>([])
const formRef = ref()

const amenityInput = ref('')

const addAmenity = () => {
  const val = amenityInput.value.trim()
  if (val && !form.value.amenities.includes(val)) {
    form.value.amenities.push(val)
  }
  amenityInput.value = ''
}

const removeAmenity = (index: number) => {
  form.value.amenities.splice(index, 1)
}

// Handle image upload preview
const onFileChange = ({ fileList: newFileList }: { fileList: any[] }) => {
  fileList.value = newFileList

  if (newFileList.length > 0 && newFileList[0].originFileObj) {
    const file = newFileList[0].originFileObj
    pendingImageFile.value = file
    const reader = new FileReader()
    reader.onload = (e) => {
      imagePreview.value = e.target?.result as string
    }
    reader.readAsDataURL(file)
  } else {
    pendingImageFile.value = null
    imagePreview.value = ''
  }
}

// Use company defaults for new properties
const getCompanyDefaults = (companyId: string) => {
  const company = props.companies.find((c) => c.id === companyId) as any
  return {
    check_in_time: company?.default_check_in_time || '15:00',
    check_out_time: company?.default_check_out_time || '11:00',
  }
}

watch(
  () => form.value.company_id,
  (newId) => {
    if (!newId || props.property?.id) return
    const defaults = getCompanyDefaults(newId)
    // Only auto-fill if times are still default 15:00/11:00 (not manually changed)
    if (form.value.check_in_time === '15:00' || !form.value.check_in_time) {
      form.value.check_in_time = defaults.check_in_time
    }
    if (form.value.check_out_time === '11:00' || !form.value.check_out_time) {
      form.value.check_out_time = defaults.check_out_time
    }
  },
)

// Reset form when dialog is opened/closed
watch(dialog, (newVal) => {
  if (newVal) {
    const firstCompanyDefaults = props.companies[0]
      ? getCompanyDefaults(props.companies[0].id)
      : { check_in_time: '15:00', check_out_time: '11:00' }
    const defaultProperty = {
      id: null,
      company_id: props.companies[0]?.id || '',
      title: '',
      location: '',
      price: null,
      type: 'Apartment',
      beds: 1,
      baths: 1,
      sqm: 0,
      description: '',
      amenities: [],
      status: 'active',
      featured: false,
      image: '',
      rental_types: ['nightly'],
      check_in_time: firstCompanyDefaults.check_in_time,
      check_out_time: firstCompanyDefaults.check_out_time,
    }
    form.value = props.property
      ? {
          ...props.property,
          amenities: [...(props.property.amenities || [])],
          check_in_time:
            (props.property as any).check_in_time ||
            getCompanyDefaults((props.property as any).company_id).check_in_time,
          check_out_time:
            (props.property as any).check_out_time ||
            getCompanyDefaults((props.property as any).company_id).check_out_time,
        }
      : defaultProperty
    const img = props.property?.image || ''
    getDisplayUrl(img).then((url) => {
      imagePreview.value = url
    })
    fileList.value = []
  }
})

const submit = async () => {
  try {
    await formRef.value.validate()

    let image = form.value.image || ''
    if (pendingImageFile.value) {
      const { key } = await uploadStorageFile(
        STORAGE_FOLDERS.PROPERTY_PHOTOS,
        `${form.value.title || 'property'}-${Date.now()}-${pendingImageFile.value.name}`,
        pendingImageFile.value,
        pendingImageFile.value.type,
      )
      image = key
    }

    const payload: Partial<Property> = {
      id: form.value.id || undefined,
      company_id: form.value.company_id,
      title: form.value.title,
      location: form.value.location,
      price: Number(form.value.price || 0),
      type: form.value.type,
      beds: Number(form.value.beds || 0),
      baths: Number(form.value.baths || 0),
      sqm: Number(form.value.sqm || 0),
      description: form.value.description || '',
      status: form.value.status || 'active',
      featured: !!form.value.featured,
      rental_types: form.value.rental_types || ['nightly'],
      check_in_time: form.value.check_in_time || '15:00',
      check_out_time: form.value.check_out_time || '11:00',
      image,
    }

    emit('save', payload)
  } catch (error) {
    console.error('Validation failed:', error)
  }
}
</script>

<template>
  <Modal
    v-model:open="dialog"
    :title="property?.id ? 'Edit Property' : 'Add New Property'"
    width="800px"
    :footer="null"
    @cancel="dialog = false"
  >
    <Form ref="formRef" :model="form" layout="vertical" @finish="submit">
      <!-- Image Upload at the top -->
      <FormItem label="Property Image" style="margin-bottom: 24px">
        <div class="d-flex align-start">
          <Upload
            v-model:file-list="fileList"
            :max-count="1"
            :before-upload="() => false"
            list-type="picture-card"
            @change="onFileChange"
            style="margin-right: 16px"
          >
            <div v-if="fileList.length === 0">
              <PlusOutlined />
              <div style="margin-top: 8px">Upload</div>
            </div>
          </Upload>

          <div v-if="imagePreview || form.image" class="image-preview">
            <img
              :src="imagePreview || form.image"
              alt="Property"
              style="width: 120px; height: 120px; object-fit: cover; border-radius: 8px"
            />
          </div>
        </div>
      </FormItem>

      <!-- Form Fields in single column -->
      <a-row :gutter="16">
        <a-col :span="24">
          <FormItem
            label="Company"
            name="company_id"
            :rules="[{ required: true, message: 'Please select company!' }]"
          >
            <Select v-model:value="form.company_id" placeholder="Select company">
              <Select.Option v-for="company in companies" :key="company.id" :value="company.id">
                {{ company.name }}
              </Select.Option>
            </Select>
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem
            label="Property Title"
            name="title"
            :rules="[{ required: true, message: 'Please input property title!' }]"
          >
            <Input v-model:value="form.title" placeholder="Enter property title" />
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem
            label="Property Type"
            name="type"
            :rules="[{ required: true, message: 'Please select property type!' }]"
          >
            <Select v-model:value="form.type" placeholder="Select property type">
              <Select.Option v-for="type in propertyTypes" :key="type" :value="type">
                {{ type }}
              </Select.Option>
            </Select>
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem
            label="Location"
            name="location"
            :rules="[{ required: true, message: 'Please input location!' }]"
          >
            <Input v-model:value="form.location" placeholder="Enter location" />
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem
            label="Price"
            name="price"
            :rules="[{ required: true, message: 'Please input price!' }]"
          >
            <InputNumber
              v-model:value="form.price"
              :formatter="(value: any) => `₱${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              :parser="(value: any) => value!.replace(/₱\s?|(,*)/g, '')"
              :min="0"
              style="width: 100%"
              placeholder="Enter price"
            />
          </FormItem>
        </a-col>

        <a-col :span="8">
          <FormItem label="Beds" name="beds" :rules="[{ required: true, message: 'Beds required' }]">
            <InputNumber
              v-model:value="form.beds"
              :min="0"
              style="width: 100%"
              placeholder="Beds"
            />
          </FormItem>
        </a-col>

        <a-col :span="8">
          <FormItem label="Baths" name="baths" :rules="[{ required: true, message: 'Baths required' }]">
            <InputNumber
              v-model:value="form.baths"
              :min="0"
              :step="0.5"
              style="width: 100%"
              placeholder="Baths"
            />
          </FormItem>
        </a-col>

        <a-col :span="8">
          <FormItem label="Area (m²)" name="sqm" :rules="[{ required: true, message: 'Area required' }]">
            <InputNumber
              v-model:value="form.sqm"
              :min="0"
              style="width: 100%"
              placeholder="Area in square meters"
            />
          </FormItem>
        </a-col>

        <a-col :span="12">
          <FormItem label="Check-in Time" name="check_in_time" :rules="[{ required: true, message: 'Check-in required' }]">
            <TimePicker v-model:value="form.check_in_time" format="HH:mm" valueFormat="HH:mm" style="width: 100%" placeholder="15:00" />
          </FormItem>
        </a-col>
        <a-col :span="12">
          <FormItem label="Check-out Time" name="check_out_time" :rules="[{ required: true, message: 'Check-out required' }]">
            <TimePicker v-model:value="form.check_out_time" format="HH:mm" valueFormat="HH:mm" style="width: 100%" placeholder="11:00" />
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem label="Description" name="description" :rules="[{ required: true, message: 'Description required' }]">
            <Input.TextArea
              v-model:value="form.description"
              :rows="3"
              placeholder="Enter property description"
            />
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem label="Amenities">
            <div style="display: flex; gap: 8px; margin-bottom: 8px">
              <Input v-model:value="amenityInput" placeholder="Add an amenity" @press-enter="addAmenity()" />
              <a-button @click="addAmenity">Add</a-button>
            </div>
            <a-flex wrap="wrap" gap="4">
              <a-tag
                v-for="(amenity, i) in form.amenities"
                :key="i"
                closable
                @close="removeAmenity(Number(i))"
              >
                {{ amenity }}
              </a-tag>
              <span v-if="form.amenities.length === 0" style="color: rgba(0,0,0,0.35); font-size: 13px">No amenities added yet</span>
            </a-flex>
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem label="Status" name="status">
            <Select v-model:value="form.status" placeholder="Select status">
              <Select.Option v-for="status in propertyStatus" :key="status" :value="status">
                {{ status }}
              </Select.Option>
            </Select>
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem label="Rental Types" name="rental_types">
            <Select v-model:value="form.rental_types" mode="multiple" placeholder="Select rental types">
              <Select.Option value="nightly">Nightly</Select.Option>
              <Select.Option value="monthly">Monthly</Select.Option>
              <Select.Option value="lease">Lease</Select.Option>
            </Select>
          </FormItem>
        </a-col>

        <a-col :span="24">
          <FormItem name="featured">
            <Switch v-model:checked="form.featured" />
            <span style="margin-left: 8px">Featured Property</span>
          </FormItem>
        </a-col>
      </a-row>

      <FormItem style="margin-bottom: 0; margin-top: 24px">
        <div style="text-align: right">
          <Button style="margin-right: 8px" @click="dialog = false"> Cancel </Button>
          <Button type="primary" html-type="submit" :loading="saving">
            {{ property?.id ? 'Update' : 'Save' }} Property
          </Button>
        </div>
      </FormItem>
    </Form>
  </Modal>
</template>
